import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Video, Loader2, CheckCircle2, Camera, FolderOpen, X, Home as HomeIcon,
} from "lucide-react";
import { api, fileUrl, formatApiError, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import MobileScreenHeader from "../components/MobileScreenHeader";

const MAX_MB = 300;

/** Simplified Create Reel flow —
 *  Land → Camera/Gallery (inline) → Upload → (auto-link or quick property pick) → Caption → Publish.
 *  Property picker only appears if the user has multiple properties AND none was preselected via ?property=.
 */
export default function ReelsCreate() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const preselectId = params.get("property");

    const [properties, setProperties] = useState([]);
    const [loadingProps, setLoadingProps] = useState(true);
    const [property, setProperty] = useState(null);

    const [videoUrl, setVideoUrl] = useState(null);
    const [videoPath, setVideoPath] = useState(null);
    const [videoPreview, setVideoPreview] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    const [uploadBytes, setUploadBytes] = useState({ loaded: 0, total: 0 });
    const [uploadError, setUploadError] = useState(null);
    const [lastFile, setLastFile] = useState(null);

    const [caption, setCaption] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [showPropertySheet, setShowPropertySheet] = useState(false);

    const galleryRef = useRef(null);
    const cameraRef  = useRef(null);

    // Redirect if not logged in
    useEffect(() => {
        if (!user) navigate("/login?next=/reels/create", { replace: true });
    }, [user, navigate]);

    // Load my properties in the background (needed for linking)
    useEffect(() => {
        if (!user) return;
        api.get("/me/properties")
            .then((r) => {
                const list = r.data?.items || r.data || [];
                setProperties(list);
                // Auto-pick property
                if (preselectId) {
                    const found = list.find((p) => p.id === preselectId);
                    if (found) setProperty(found);
                } else if (list.length === 1) {
                    setProperty(list[0]);
                }
            })
            .catch(() => setProperties([]))
            .finally(() => setLoadingProps(false));
    }, [user, preselectId]);

    const onFilePick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await doUpload(file);
    };

    const doUpload = async (file) => {
        if (!/^video\//.test(file.type)) { toast.error("Please pick a video file"); return; }
        if (file.size > MAX_MB * 1024 * 1024) {
            toast.error(`Video must be ${MAX_MB} MB or smaller.`);
            return;
        }
        setLastFile(file);
        setVideoPreview(URL.createObjectURL(file));
        setUploading(true);
        setUploadPct(0);
        setUploadError(null);
        setUploadBytes({ loaded: 0, total: file.size });
        try {
            const form = new FormData();
            form.append("file", file);
            const res = await api.post("/reels/upload-video", form, {
                headers: { "Content-Type": "multipart/form-data" },
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    const pct = Math.round((evt.loaded * 100) / evt.total);
                    setUploadPct(pct);
                    setUploadBytes({ loaded: evt.loaded, total: evt.total });
                },
                // Give large uploads plenty of time — up to ~15 min on very slow connections
                timeout: 15 * 60 * 1000,
            });
            setVideoUrl(res.data?.url);
            setVideoPath(res.data?.path);
            setUploadPct(100);
            toast.success("Video uploaded successfully");
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message || "Upload failed";
            setUploadError(msg);
            toast.error(msg);
        } finally {
            setUploading(false);
        }
    };

    const retryUpload = () => { if (lastFile) doUpload(lastFile); };

    const submit = async () => {
        if (!videoUrl) { toast.error("Please add a video first"); return; }
        setSubmitting(true);
        try {
            await api.post("/reels", {
                property_id: property?.id || null,
                video_url: videoUrl,
                video_path: videoPath,
                thumbnail_url: (property?.images || [])[0] || null,
                caption: caption.trim() || null,
            });
            toast.success("Reel published — it's now live on GPM Reels!");
            navigate("/my-reels");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not publish reel");
        } finally {
            setSubmitting(false);
        }
    };

    // No properties → still allow creating an unlinked reel
    // (linking is optional now — user just skips the property step)

    return (
        <div className="min-h-screen bg-[#f6f7fb] pb-24" data-testid="reels-create-page">
            <MobileScreenHeader title="Create Reel" subtitle="Record or pick from gallery" testid="create-reel-header" />

            <div className="px-4 mt-4">
                {/* ---------- Video area ---------- */}
                <div className="rounded-2xl bg-white p-4 border" style={{ borderColor: "rgba(0,38,87,0.1)" }}>
                    {!videoPreview ? (
                        <div className="grid grid-cols-2 gap-2" data-testid="create-source-inline">
                            <button
                                onClick={() => cameraRef.current?.click()}
                                data-testid="create-source-camera"
                                className="flex flex-col items-center gap-2 py-6 rounded-2xl border"
                                style={{ background: "#FBF6E0", borderColor: "rgba(218,165,32,0.35)" }}
                            >
                                <div className="w-14 h-14 rounded-full flex items-center justify-center"
                                     style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)" }}>
                                    <Camera className="w-7 h-7 text-white" />
                                </div>
                                <div className="font-heading font-extrabold text-[13px]" style={{ color: "#002657" }}>Camera</div>
                                <div className="text-[10.5px]" style={{ color: "rgba(0,38,87,0.6)" }}>
                                    Record now · max {MAX_MB} MB
                                </div>
                            </button>
                            <button
                                onClick={() => galleryRef.current?.click()}
                                data-testid="create-source-gallery"
                                className="flex flex-col items-center gap-2 py-6 rounded-2xl border"
                                style={{ background: "#EEF2FF", borderColor: "rgba(37,99,235,0.25)" }}
                            >
                                <div className="w-14 h-14 rounded-full flex items-center justify-center"
                                     style={{ background: "linear-gradient(135deg, #2563EB, #1D4ED8)" }}>
                                    <FolderOpen className="w-7 h-7 text-white" />
                                </div>
                                <div className="font-heading font-extrabold text-[13px]" style={{ color: "#002657" }}>Gallery</div>
                                <div className="text-[10px]" style={{ color: "rgba(0,38,87,0.6)" }}>Pick from phone · max {MAX_MB} MB</div>
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="relative w-full max-w-[240px] mx-auto aspect-[9/16] rounded-xl overflow-hidden border"
                                 style={{ background: "#f6f7fb", borderColor: "rgba(0,38,87,0.1)" }}>
                                <video src={videoPreview} controls playsInline className="w-full h-full object-contain" data-testid="create-video-preview" />
                                {uploading && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3"
                                         style={{ background: "rgba(255,255,255,0.92)", color: "#002657" }}>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <div className="font-heading font-extrabold text-[12px]">Uploading… {uploadPct}%</div>
                                        <div className="w-full max-w-[180px] h-2 rounded-full overflow-hidden" style={{ background: "#e5e7eb" }}>
                                            <div className="h-full transition-all"
                                                 style={{ width: `${uploadPct}%`, background: "linear-gradient(90deg, #DAA520, #F59E0B)" }} />
                                        </div>
                                        <div className="text-[10px]" style={{ color: "rgba(0,38,87,0.6)" }}>
                                            {(uploadBytes.loaded / (1024*1024)).toFixed(1)} MB / {(uploadBytes.total / (1024*1024)).toFixed(1)} MB
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* File name / size / status */}
                            {lastFile && (
                                <div className="mt-2 text-[10.5px] px-2" style={{ color: "rgba(0,38,87,0.75)" }} data-testid="create-file-meta">
                                    <div className="truncate"><span className="font-bold">{lastFile.name}</span></div>
                                    <div>
                                        {(lastFile.size / (1024*1024)).toFixed(1)} MB
                                        {videoUrl && !uploading && <span className="ml-1" style={{ color: "#059669" }}>· ✓ Uploaded</span>}
                                        {uploadError && <span className="ml-1" style={{ color: "#DC2626" }}>· Failed</span>}
                                    </div>
                                </div>
                            )}
                            {uploadError && !uploading && (
                                <button onClick={retryUpload} data-testid="create-upload-retry"
                                    className="mt-2 w-full py-2 rounded-lg border text-[11.5px] font-bold"
                                    style={{ borderColor: "rgba(220,38,38,0.4)", color: "#DC2626", background: "#FEF2F2" }}>
                                    Retry Upload
                                </button>
                            )}
                            {!uploading && (
                                <div className="mt-3 grid grid-cols-2 gap-2">
                                    <button onClick={() => cameraRef.current?.click()} data-testid="create-change-camera"
                                        className="py-2 rounded-lg border text-[11.5px] font-bold flex items-center justify-center gap-1"
                                        style={{ borderColor: "rgba(0,38,87,0.15)", color: "#002657" }}>
                                        <Camera className="w-3.5 h-3.5" /> Camera
                                    </button>
                                    <button onClick={() => galleryRef.current?.click()} data-testid="create-change-gallery"
                                        className="py-2 rounded-lg border text-[11.5px] font-bold flex items-center justify-center gap-1"
                                        style={{ borderColor: "rgba(0,38,87,0.15)", color: "#002657" }}>
                                        <FolderOpen className="w-3.5 h-3.5" /> Gallery
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                    <input ref={galleryRef} type="file" accept="video/*" hidden onChange={onFilePick} data-testid="create-video-input" />
                    <input ref={cameraRef}  type="file" accept="video/*" capture="environment" hidden onChange={onFilePick} data-testid="create-video-camera-input" />
                    <div className="mt-2 text-[10px] text-center" style={{ color: "rgba(0,38,87,0.55)" }}>
                        Maximum video size: {MAX_MB} MB
                    </div>
                </div>

                {/* ---------- Linked property card (OPTIONAL) ---------- */}
                {videoUrl && (
                    <div className="mt-4 rounded-2xl bg-white p-3 border" style={{ borderColor: "rgba(0,38,87,0.1)" }} data-testid="create-linked-property">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10.5px] uppercase tracking-widest font-extrabold" style={{ color: "#8B6712" }}>
                                Link a property <span className="normal-case tracking-normal" style={{ color: "rgba(0,38,87,0.5)" }}>(optional)</span>
                            </div>
                            {property && (
                                <button onClick={() => setProperty(null)} data-testid="create-unlink-property"
                                    className="text-[10px] font-bold underline" style={{ color: "#DC2626" }}>
                                    Unlink
                                </button>
                            )}
                        </div>
                        {property ? (
                            <div className="flex items-center gap-3">
                                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0" style={{ background: "#FBF6E0" }}>
                                    {property.images?.[0] ? <img src={fileUrl(property.images[0])} alt="" className="w-full h-full object-cover" /> : null}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading font-bold text-[12.5px] truncate" style={{ color: "#002657" }}>{property.title}</div>
                                    <div className="text-[10.5px] truncate" style={{ color: "rgba(0,38,87,0.6)" }}>
                                        {[property.locality, property.city].filter(Boolean).join(", ")}
                                    </div>
                                    <div className="text-[10.5px] font-extrabold" style={{ color: "#F59E0B" }}>{formatINR(property.price)}</div>
                                </div>
                                {properties.length > 1 && (
                                    <button onClick={() => setShowPropertySheet(true)} data-testid="create-change-property"
                                        className="px-2 py-1 rounded-md text-[10.5px] font-bold border"
                                        style={{ borderColor: "rgba(0,38,87,0.15)", color: "#002657" }}>
                                        Change
                                    </button>
                                )}
                            </div>
                        ) : properties.length > 0 ? (
                            <button onClick={() => setShowPropertySheet(true)} data-testid="create-choose-property"
                                className="w-full py-2 rounded-lg text-[12px] font-bold border"
                                style={{ borderColor: "rgba(218,165,32,0.5)", color: "#8B6712", background: "#FBF6E0" }}>
                                + Link one of your properties
                            </button>
                        ) : (
                            <div className="text-[10.5px] text-center py-1" style={{ color: "rgba(0,38,87,0.55)" }}>
                                No listings yet — publishing without link is fine, or <button onClick={() => navigate("/add-property")} className="underline font-bold" style={{ color: "#8B6712" }}>list a property</button> first.
                            </div>
                        )}
                    </div>
                )}

                {/* ---------- Caption ---------- */}
                {videoUrl && (
                    <div className="mt-4">
                        <div className="text-[10.5px] uppercase tracking-widest font-extrabold mb-2" style={{ color: "#8B6712" }}>
                            Caption (optional)
                        </div>
                        <textarea
                            value={caption} onChange={(e) => setCaption(e.target.value)}
                            placeholder="Highlight the best about this property in one line…"
                            maxLength={300}
                            rows={3}
                            data-testid="create-caption"
                            className="w-full px-3 py-2.5 rounded-xl border text-[12.5px] bg-white"
                            style={{ borderColor: "rgba(0,38,87,0.15)" }}
                        />
                        <div className="text-right text-[10px]" style={{ color: "rgba(0,38,87,0.5)" }}>{caption.length}/300</div>
                    </div>
                )}

                {/* ---------- Publish CTA ---------- */}
                {videoUrl && (
                    <button
                        onClick={submit}
                        disabled={submitting || uploading}
                        data-testid="create-submit"
                        className="mt-4 w-full py-3 rounded-xl text-[13px] font-extrabold shadow flex items-center justify-center gap-1"
                        style={{
                            background: !uploading ? "linear-gradient(90deg, #DAA520, #F59E0B)" : "rgba(0,38,87,0.15)",
                            color: !uploading ? "#001a3d" : "white",
                        }}
                    >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-3.5 h-3.5" />}
                        Publish Reel
                    </button>
                )}

                <div className="mt-3 text-[10px] text-center" style={{ color: "rgba(0,38,87,0.55)" }}>
                    Reels go live instantly. Please keep the video relevant to the linked property.
                </div>
            </div>

            {/* ---------- Property picker bottom sheet ---------- */}
            {showPropertySheet && (
                <div className="fixed inset-0 z-40 flex items-end" style={{ background: "rgba(0,0,0,0.55)" }}
                     onClick={() => setShowPropertySheet(false)} data-testid="create-property-sheet">
                    <div className="w-full bg-white rounded-t-2xl p-4 max-h-[75vh] overflow-y-auto"
                         onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>Which property is this Reel for?</div>
                            <button onClick={() => setShowPropertySheet(false)}
                                    data-testid="create-property-sheet-close"
                                    className="w-8 h-8 rounded-full flex items-center justify-center"
                                    style={{ background: "#f4f4f5" }}>
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="space-y-2">
                            {properties.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => { setProperty(p); setShowPropertySheet(false); }}
                                    data-testid={`create-pick-property-${p.id}`}
                                    className="w-full flex items-center gap-3 rounded-xl bg-white p-2.5 border shadow-sm text-left"
                                    style={{ borderColor: property?.id === p.id ? "#DAA520" : "rgba(0,38,87,0.08)" }}
                                >
                                    <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0" style={{ background: "#FBF6E0" }}>
                                        {p.images?.[0] ? <img src={fileUrl(p.images[0])} alt="" className="w-full h-full object-cover" /> : null}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-heading font-bold text-[12px] truncate" style={{ color: "#002657" }}>{p.title}</div>
                                        <div className="text-[10px] truncate" style={{ color: "rgba(0,38,87,0.6)" }}>
                                            {[p.locality, p.city].filter(Boolean).join(", ")}
                                        </div>
                                        <div className="text-[10px] font-extrabold" style={{ color: "#F59E0B" }}>{formatINR(p.price)}</div>
                                    </div>
                                    {property?.id === p.id && <CheckCircle2 className="w-5 h-5" style={{ color: "#DAA520" }} />}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
